import type { DayOption } from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { formatDate, useLocale, useT } from '../lib/i18n.js'
import { Button, ErrorNotice, Input } from './ui.js'

/**
 * "I wish there was a way to copy just day two."
 *
 * That sentence is the whole specification, and the important thing about it is
 * what it does NOT ask for. It does not ask for a library of saved day
 * templates. Day two of a trip she actually built is already the template; it
 * only needed a way to be reached. A parallel store of blocks would have to be
 * maintained alongside the trips they came from, and would drift out of date
 * the first time she improved the real one.
 *
 * So the picker offers days — real ones, from her other trips, labelled with the
 * trip they belong to and what is on them. Empty days are filtered out on the
 * server: a blank Tuesday is not a template.
 */
export function CopyDayButton({
  tripId,
  dayId,
  onCopied,
}: {
  tripId: string
  dayId: string
  onCopied(): Promise<void> | void
}) {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const options = useQuery({
    queryKey: ['dayOptions', tripId],
    queryFn: () => api.listDayOptions(tripId),
    enabled: open,
  })

  const copy = useMutation({
    mutationFn: (sourceDayId: string) => api.copyDayFrom(dayId, { sourceDayId }),
    async onSuccess() {
      await onCopied()
      // Copying changes what the picker would offer next time, since the day it
      // landed on now has content of its own.
      await queryClient.invalidateQueries({ queryKey: ['dayOptions', tripId] })
      setOpen(false)
    },
  })

  const shown = useMemo(() => {
    const all = options.data ?? []
    const needle = query
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim()
    if (!needle) return all
    return all.filter((option) =>
      `${option.tripTitle} ${option.date}`
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .includes(needle),
    )
  }, [options.data, query])

  if (!open) {
    return (
      <Button variant="quiet" onClick={() => setOpen(true)}>
        {t('copyDay.open')}
      </Button>
    )
  }

  return (
    <div
      data-testid="copy-day-picker"
      className="flex flex-col gap-3 rounded-md bg-slate-50 p-3"
    >
      <p className="text-xs text-slate-500">{t('copyDay.hint')}</p>

      {options.isPending && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
      {options.isError && <ErrorNotice error={options.error} />}
      {options.data?.length === 0 && (
        <p className="text-sm text-slate-500">{t('copyDay.empty')}</p>
      )}

      {(options.data?.length ?? 0) > 0 && (
        <Input
          type="search"
          value={query}
          aria-label={t('copyDay.search')}
          placeholder={t('copyDay.search')}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}

      {copy.isError && <ErrorNotice error={copy.error} />}

      {shown.length > 0 && (
        <ul className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
          {shown.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                disabled={copy.isPending}
                onClick={() => copy.mutate(option.id)}
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-white disabled:opacity-50"
              >
                <span className="font-medium">{option.tripTitle}</span>
                <span className="text-xs text-slate-500">
                  {formatDate(option.date, locale)} · {summarise(option, t)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div>
        <Button type="button" variant="quiet" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}

/**
 * What is on the day, so she can tell day two from day three without opening
 * the other trip. The counts are the only thing that distinguishes them.
 */
function summarise(option: DayOption, t: ReturnType<typeof useT>): string {
  const parts: string[] = []
  if (option.itemCount > 0) parts.push(t('day.itemCount', { count: option.itemCount }))
  if (option.hasNotes) parts.push(t('copyDay.withNotes'))
  return parts.join(' · ')
}
